import { useState } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { Toast } from './Toast';

export function OnlineStatusIndicator() {
  const isOnline = useOnlineStatus();
  const { isUpdateAvailable, isChecking, skipWaiting, checkForUpdates } = useServiceWorker();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleOnlineClick = () => {
    setToastMessage(isOnline ? "You're online" : "You're offline");
    setShowToast(true);
  };

  const handleUpdateClick = async () => {
    if (isUpdateAvailable) {
      setToastMessage('Updating app...');
      setShowToast(true);
      skipWaiting();
    } else if (isChecking) {
      // Already checking, do nothing
      return;
    } else {
      // No update available - check for updates
      setToastMessage('Checking for updates...');
      setShowToast(true);
      await checkForUpdates();
      // After check, show result
      if (!isUpdateAvailable) {
        setToastMessage('App is up to date');
        setShowToast(true);
      }
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const onlineIcon = isOnline ? <Wifi size={24} /> : <WifiOff size={24} />;
  const onlineColor = isOnline ? '#22c55e' : '#ef4444';

  const updateColor = isUpdateAvailable ? '#f97316' : '#22c55e';

  return (
    <>
      <button
        onClick={handleOnlineClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          color: onlineColor,
          transition: 'all 200ms ease-in-out',
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title={isOnline ? 'Online' : 'Offline'}
        aria-label={isOnline ? 'Online' : 'Offline'}
      >
        {onlineIcon}
      </button>

      <button
        onClick={handleUpdateClick}
        disabled={isChecking}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: isChecking ? 'wait' : 'pointer',
          color: updateColor,
          transition: 'all 200ms ease-in-out',
          padding: 0,
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title={isUpdateAvailable ? 'Update available' : isChecking ? 'Checking...' : 'Check for updates'}
        aria-label={isUpdateAvailable ? 'Update available' : isChecking ? 'Checking for updates' : 'Check for updates'}
      >
        <RefreshCw
          size={24}
          style={{
            animation: isChecking ? 'spin 1s linear infinite' : 'none',
          }}
        />
        {isUpdateAvailable && (
          <div
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '8px',
              height: '8px',
              backgroundColor: '#f97316',
              borderRadius: '50%',
            }}
          />
        )}
      </button>

      <Toast message={toastMessage} isVisible={showToast} onClose={handleCloseToast} />
    </>
  );
}
